import React from "react"
import { Clock, CheckCircle2, UtensilsCrossed, Timer } from "lucide-react"
import { useOrderTimer } from "../../hooks/useOrderTimer"
import type { Order } from "../../types"
import { useServicePalette } from "../../hooks/useServicePalette"

export const getStatusText = (status: string) => {
    const statusMap: { [key: string]: string } = {
        RECEIVED: "Recibida",
        ACEPTED: "Aceptada",
        IN_PREPARATION: "En preparación",
        DELIVERY_PENDING: "Pendiente de entrega",
        DELIVERED: "Entregada",
        CANCELED: "Cancelada",
    }
    return statusMap[status] || status
}

export const getStatusIcon = (status: string) => {
    switch (status) {
        case "RECEIVED":
            return <Clock className="w-4 h-4" />
        case "ACEPTED":
            return <CheckCircle2 className="w-4 h-4" />
        case "IN_PREPARATION":
            return <UtensilsCrossed className="w-4 h-4" />
        case "DELIVERED":
            return <CheckCircle2 className="w-4 h-4" />
        default:
            return <Clock className="w-4 h-4 text-gray-500" />
    }
}

interface OrderCardProps {
    order: Order;
    palette: ReturnType<typeof useServicePalette>;
    isTakeAway: boolean;
}

export const OrderCard: React.FC<OrderCardProps> = ({ order, palette, isTakeAway }) => {
    const timer = useOrderTimer(order);

    const formatScheduledDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-AR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusMessage = () => {
        if (order.scheduled_at) {
            return `Tu orden está programada para ${formatScheduledDate(order.scheduled_at)}`;
        }

        if (isTakeAway) {
            if (order.status === 'RECEIVED') return "Esperando confirmación...";
            if (order.status === 'DELIVERED') return "Orden entregada";
            if (timer?.isOverdue || (order.status === 'ACEPTED' && !timer)) return "";
            return "Tu orden estará lista en...";
        }

        return getStatusText("Pronto llevarán tu orden");
    };

    return (
        <div className={`backdrop-blur-sm rounded-2xl shadow-lg transition-all duration-300 bg-white/80 dark:bg-gray-800/80 ${palette.borderAccent} overflow-hidden`}>
            <div className="p-4">
                <div className="flex justify-between items-start mb-3">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                            Orden #{order.order_number}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {getStatusMessage()}
                        </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        <div className="flex items-center gap-2 px-3 py-1 rounded-full backdrop-blur-sm bg-gray-100/80 dark:bg-gray-700/30">
                            {getStatusIcon(order.status || '')}
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                {order.scheduled_at ? 'Programada' : getStatusText(order.status || '')}
                            </span>
                        </div>
                        {timer && !timer.isOverdue && !order.scheduled_at && order.status !== 'RECEIVED' && (
                            <div className="flex items-center gap-2 text-sky-600 dark:text-sky-400 font-mono font-bold bg-sky-50 dark:bg-sky-900/30 px-3 py-1 rounded-lg">
                                <Timer className="w-4 h-4" />
                                <span>{timer.timeLeft}</span>
                            </div>
                        )}
                    </div>
                </div>

                {timer && !timer.isOverdue && !order.scheduled_at && order.status !== 'RECEIVED' && (
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mb-4 overflow-hidden">
                        <div
                            className="bg-sky-500 h-1.5 rounded-full transition-all duration-1000 ease-linear"
                            style={{ width: `${timer.progress}%` }}
                        />
                    </div>
                )}

                <div className="space-y-2">
                    {order.order_products.map((item, index) => {
                        const price = item.product_details?.price || item.offer_details?.price || 0;
                        const subProducts = item.product_details?.sub_products || [];

                        return (
                            <div
                                key={index}
                                className={`flex flex-col gap-1.5 rounded-xl p-3 ${palette.bgLight} dark:bg-gray-900/20 border border-black/5 dark:border-white/5 transition-all`}
                            >
                                <div className="flex justify-between items-start">
                                    <span className="font-bold text-sm text-gray-800 dark:text-gray-200 leading-tight">
                                        <span className="opacity-70 font-extrabold mr-1.5">{item.quantity}x</span>
                                        {item.product_details?.name || item.offer_details?.name || "Producto"}
                                    </span>
                                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 ml-2">
                                        ${(price * item.quantity).toFixed(2)}
                                    </span>
                                </div>
                                {subProducts.length > 0 && Array.isArray(subProducts) && (
                                    <div className="flex flex-wrap gap-1 px-5">
                                        {subProducts.map((sub: any, idx: number) => (
                                            <span key={sub.uuid || sub.sub_product_uuid || idx} className="text-[10px] bg-white/60 dark:bg-black/20 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded-md font-bold uppercase tracking-wider border border-black/5 dark:border-white/5">
                                                {sub.name || sub.sub_product_name}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {/* {item.observation && (
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 italic px-5 leading-snug">
                                        💬 "{item.observation}"
                                    </p>
                                )} */}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
